import { Link } from '@tanstack/react-router'
import { useRouterState } from '@tanstack/react-router'
import { Divider, Layout } from 'antd'
import clsx from 'clsx'
import { useSetAtom } from 'jotai'
import { useAtomCallback } from 'jotai/utils'
import { useCallback, useState, type FC } from 'react'
import { FaUpload, FaHistory } from 'react-icons/fa'

import { PopoverListGroupTitle, PopoverListItem } from '@/components/elements'
import { ACTIONS, LABELS } from '@/constants/common/message.constants'
import { type UserMeTenantExEntity, type UserMeExEntity } from '@/libs/api'
import { USER_TYPE } from '@/libs/api/types'

import { useTmsVisibility } from '../../-hooks'
import {
  headerPortalElementAtom,
  isOpenShipmentOrderCsvImportModalAtom,
  isOpenTourCsvImportModalAtom
} from '../../-states'
import { AppBreadcrumb } from '../AppBreadcrumb'
import { AuthUserPopoverTrigger } from '../AuthUserPopoverTrigger'
import { HeaderItem } from '../HeaderItem'
import { PartnerUserPopoverTrigger } from '../PartnerUserPopoverTrigger'

const { Header: AntdHeader } = Layout

type Props = {
  authUser: UserMeExEntity
  currentTenant: UserMeTenantExEntity
  onLogout: () => void
  tenantKey: string
}

export const Header: FC<Props> = ({
  authUser,
  currentTenant,
  onLogout,
  tenantKey
}) => {
  const setHeaderPortalElement = useSetAtom(headerPortalElementAtom)
  const [openImportJobsPopover, setOpenImportJobsPopover] = useState(false)
  const isUsingTms = useTmsVisibility(tenantKey, authUser)
  const handleImportJobsPopoverItem = useCallback(() => {
    setOpenImportJobsPopover(false)
  }, [])

  const handleOpenTourCsvImportModal = useAtomCallback(
    useCallback(
      (_, set) => {
        set(isOpenTourCsvImportModalAtom, true)
        handleImportJobsPopoverItem()
      },
      [handleImportJobsPopoverItem]
    )
  )

  const handleOpenShipmentOrderImportModal = useAtomCallback(
    useCallback(
      (_, set) => {
        set(isOpenShipmentOrderCsvImportModalAtom, true)
        handleImportJobsPopoverItem()
      },
      [handleImportJobsPopoverItem]
    )
  )

  const sticky = useRouterState({
    select: (state) =>
      state.matches.some((match) => match.staticData.stickyHeader === true)
  })

  // 配車板ページの場合は、右側の固定メニュー(49px)に重ならないように
  // ヘッダー右側の操作アイコン群を 49px 左にオフセットする
  const isVehicleDispatchBoardsPage = useRouterState({
    select: (state) =>
      state.location.pathname.includes('/vehicle-dispatch-boards')
  })

  return (
    <AntdHeader
      className={clsx([
        'flex h-app-header items-center bg-white p-0',
        sticky && 'sticky top-0 z-[2]'
      ])}
    >
      <div className="flex w-full items-center gap-4 pl-6 pr-0">
        <AppBreadcrumb data-testid="breadcrumb" />
        <div className="flex-1" ref={(ref) => setHeaderPortalElement(ref)} />
        <div
          className={clsx([
            'sticky z-[3] ml-auto flex items-center gap-2 bg-white pr-8',
            isVehicleDispatchBoardsPage ? 'right-[49px]' : 'right-0'
          ])}
        >
          {authUser.type === USER_TYPE.OWNER && (
            <>
              <HeaderItem
                data-testid="import-jobs-popover"
                open={openImportJobsPopover}
                onOpenChange={setOpenImportJobsPopover}
                popoverContent={
                  <div className="-m-3">
                    <PopoverListGroupTitle className="!pt-3">
                      {LABELS.TOUR}
                    </PopoverListGroupTitle>
                    <PopoverListItem
                      icon={<FaUpload />}
                      onClick={handleOpenTourCsvImportModal}
                    >
                      {ACTIONS.IMPORT_FROM_CSV}
                    </PopoverListItem>

                    <Link
                      to="/$tenantKey/tour-import-jobs"
                      params={{
                        tenantKey: currentTenant.key
                      }}
                      onClick={handleImportJobsPopoverItem}
                    >
                      <PopoverListItem
                        icon={<FaHistory />}
                        className="[[data-status='active']_&>div]:!text-primary"
                      >
                        {LABELS.TOUR_IMPORT_HISTORY}
                      </PopoverListItem>
                    </Link>
                    {isUsingTms && (
                      <>
                        <PopoverListGroupTitle>
                          {LABELS.SHIPMENT_ORDER}
                        </PopoverListGroupTitle>
                        <PopoverListItem
                          icon={<FaUpload />}
                          onClick={handleOpenShipmentOrderImportModal}
                        >
                          {ACTIONS.IMPORT_SHIPMENT_ORDER}
                        </PopoverListItem>
                        <Link
                          to="/$tenantKey/shipment-order-import-jobs"
                          params={{
                            tenantKey: currentTenant.key
                          }}
                          onClick={handleImportJobsPopoverItem}
                        >
                          <PopoverListItem
                            icon={<FaHistory />}
                            className="[[data-status='active']_&>div]:!text-primary"
                          >
                            {LABELS.SHIPMENT_ORDER_IMPORT_HISTORY}
                          </PopoverListItem>
                        </Link>
                      </>
                    )}
                  </div>
                }
              >
                <FaUpload />
              </HeaderItem>
              <Divider type="vertical" className="m-0" />
            </>
          )}

          {/* MEMO: 通知機能は未実装 
          <HeaderItem dot={true}>
            <FaBell />
          </HeaderItem>        
          */}
          {authUser.type === USER_TYPE.PARTNER ? (
            // パートナーユーザーは拠点表示の代わりに所属パートナー名を出す (VOC_PJ-1107 / #6012)
            <PartnerUserPopoverTrigger
              authUser={authUser}
              currentTenant={currentTenant}
              onLogout={onLogout}
            />
          ) : (
            <AuthUserPopoverTrigger
              authUser={authUser}
              currentTenant={currentTenant}
              onLogout={onLogout}
            />
          )}
        </div>
      </div>
    </AntdHeader>
  )
}
